import { useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { auth, signInWithEmailAndPassword } from "../firebaseConfig.js";
import logo from "../assets/COT_logo-removebg-preview.png";
import { useSetRecoilState } from "recoil";
import { authAtom } from "../App.js";

/* ================== ANIMATIONS ================== */

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/* ================== STYLES ================== */

const Container = styled.div`
  display: flex;
  height: 100vh;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  width: 420px;
  padding: 40px 30px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(12px);
  text-align: center;
  animation: ${fadeIn} 0.5s ease;
`;

const Logo = styled.img`
  width: 90px;
  margin-bottom: 15px;
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #222;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #777;
  margin-bottom: 25px;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 18px;
`;

const Input = styled.input`
  width: 90%;
  padding: 14px 12px;
  border-radius: 10px;
  border: 1px solid #ddd;
  font-size: 14px;
  background: transparent;
  outline: none;

  &:focus {
    border-color: #2a5298;
    box-shadow: 0 0 0 2px rgba(42, 82, 152, 0.2);
  }

  &:focus + label,
  &:not(:placeholder-shown) + label {
    top: -8px;
    left: 10px;
    font-size: 11px;
    background: white;
    padding: 0 4px;
    color: #2a5298;
  }
`;

const Label = styled.label`
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  font-size: 13px;
  color: #888;
  pointer-events: none;
  transition: 0.2s ease;
`;

const PasswordToggle = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  cursor: pointer;
  color: #2a5298;
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 10px;
  background: ${({ disabled }) =>
    disabled
      ? "#ccc"
      : "linear-gradient(135deg, #1e3c72, #2a5298)"};
  color: white;
  font-size: 15px;
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  margin-top: 10px;
  transition: 0.2s;

  &:hover {
    transform: ${({ disabled }) => (disabled ? "none" : "translateY(-1px)")};
    box-shadow: ${({ disabled }) =>
      disabled ? "none" : "0 8px 20px rgba(0,0,0,0.2)"};
  }
`;

const Error = styled.p`
  color: #ff4d4f;
  font-size: 12px;
  margin-bottom: 10px;
`;

const Forgot = styled.p`
  margin-top: 12px;
  font-size: 12px;
  color: #2a5298;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Footer = styled.p`
  margin-top: 20px;
  font-size: 11px;
  color: #999;
`;

/* ================== COMPONENT ================== */

const LoginPage = () => {
  const setAuth = useSetRecoilState(authAtom);
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
  e.preventDefault();
  setError("");
  setLoading(true);

  try {
    await signInWithEmailAndPassword(auth, email, password);
    setAuth(true);
    localStorage.setItem("isAuthenticated", "true"); // ← ADD THIS
    navigate("/NavigationPage");
  } catch (err) {
    setError("Invalid email or password");
  }

  setLoading(false);
};

  return (
    <Container>
      <Card>
        <Logo src={logo} />

        <Title>Welcome Back</Title>
        <Subtitle>Login to your account</Subtitle>

        <form onSubmit={handleSubmit}>
          {error && <Error>{error}</Error>}

          <InputGroup>
            <Input
              type="email"
              placeholder=" "
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Label>Email</Label>
          </InputGroup>

          <InputGroup>
            <Input
              type={show ? "text" : "password"}
              placeholder=" "
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Label>Password</Label>
            <PasswordToggle onClick={() => setShow(!show)}>
              {show ? "Hide" : "Show"}
            </PasswordToggle>
          </InputGroup>

          <Button type="submit" disabled={loading}>
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
        <Forgot onClick={() => alert("Contact admin!")}>
          Forgot password?
        </Forgot>

        <Footer>© Cross Ocean Travel</Footer>
      </Card>
    </Container>
  );
};

export default LoginPage;